import { useState, useEffect, useMemo } from 'react'
import PropTypes from 'prop-types'
import { useTheme } from '@emotion/react'
import to from 'await-to-js'
import { rgba, shade, tint } from 'polished'
import { graphql } from 'gatsby'
import {
  Container,
  Section,
  SectionTitle,
  Button,
  List,
  ListItem,
  BlockGrid,
  Inline,
  SocialIcon,
} from 'chaoskit/src/components'
import { gradient, text } from 'chaoskit/src/assets/styles/utility'
import { generateGradient } from 'chaoskit/src/assets/styles/utility/gradient'
import { orderBy, shuffle } from 'lodash'
import useUpdateEffect from 'react-use/lib/useUpdateEffect'
import useAsync from 'react-use/lib/useAsync'
import useInterval from 'react-use/lib/useInterval'
import axios from 'axios'

import BaseLayout from '~layouts/BaseLayout'
import Data from '~components/Data'
import OverflowScroll from '~components/OverflowScroll'
import Icon from '~components/Icon'
import Loading from '~components/Loading'
import Hero, {
  HeroContent,
  HeroTitleWrapper,
  HeroTitle,
  HeroTitleSub,
} from '~partials/Hero'
import hero from '~media/team/hero.png'
import {
  textGradientUnderline,
  sectionSeparator,
  shadowEmboss,
  stickyIt,
} from '~styles/utility'
import mascotHanging from '~media/logos/mascot-hanging.svg'
import HelmetSEO from '~partials/global/HelmetSEO'
import Image from '~components/Image'
import TextLink from '~components/TextLink'
import dateFn from '~helpers/dateFn'
import cloudinaryTransform from '~helpers/cloudinary'
import ClientOnly from '~components/ClientOnly'

const StylesTeamVariables = (theme) => ({
  gridOffset: theme.space.large, // Used to provide additional offset on larger screen-sizes for text within grid
})

const TeamSection = ({ team }) => {
  const theme = useTheme()

  const employees = useMemo(() => shuffle(team.employees), [team])

  return (
    <ClientOnly>
      <Section
        space="xlarge"
        className="u-contrast"
        css={[gradient.dark(theme)]}
      >
        <Container>
          <div
            css={{
              display: 'grid',
              gap: theme.space.base,

              [theme.mq.medium]: {
                gridTemplateColumns: 'repeat(3, 1fr)',
              },
            }}
          >
            <div
              css={{
                [theme.mq.medium]: {
                  paddingRight: StylesTeamVariables(theme).gridOffset,
                  gridColumn: '1 / span 2',
                },

                [theme.mq.small__max]: {
                  marginBottom: theme.space.large,
                },
              }}
            >
              <SectionTitle
                as="h2"
                align={{ medium: 'left' }}
                title={<Data>{team.heading}</Data>}
                sub={<Data>{team.blurb}</Data>}
              />
            </div>
            {employees.map(({ employee: { frontmatter: employee } }) => (
              <EmployeeCard key={employee.title} employee={employee} />
            ))}
          </div>
        </Container>
      </Section>
    </ClientOnly>
  )
}

TeamSection.propTypes = {
  team: PropTypes.object.isRequired,
}

const EmployeeCard = ({ employee }) => {
  const theme = useTheme()

  const [isFlipped, setFlipped] = useState(false)

  /* eslint-disable jsx-a11y/click-events-have-key-events */
  return (
    <div
      role="button"
      onClick={() => setFlipped(!isFlipped)}
      tabIndex="-1"
      css={{
        perspective: 1000,
        userSelect: 'none',
      }}
    >
      <div
        css={[
          {
            transitionDuration: theme.timing.long,
            transitionTimingFunction: theme.transition.bounce,
            transformStyle: 'preserve-3d',
          },

          isFlipped && {
            transform: 'rotateY(180deg)',
          },
        ]}
      >
        <div
          css={{
            backfaceVisibility: 'hidden',
            transform: 'rotateY(0deg)',
            position: 'relative',
            cursor: 'pointer',
          }}
        >
          <Image
            css={{
              borderRadius: theme.borderRadius.base,
              transition: `transform ${theme.timing.base} ${theme.transition.bounce}`,
              transformOrigin: 'center center',
              boxShadow: theme.boxShadow.large,

              '&:hover, &:focus': {
                transform: 'scale(1.025)',
              },
            }}
            src={employee.portrait}
            alt={employee.title}
          />
        </div>
        <div
          css={[
            text.heading(theme),
            {
              backfaceVisibility: 'hidden',
              cursor: 'pointer',
              transform: 'rotateY(-180deg)',
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              background: theme.color.dark.base,
              border: `2px solid ${theme.color.light.base}`,
              borderRadius: theme.borderRadius.base,
              padding: theme.space.base,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              textAlign: 'center',
              fontSize: theme.fontSize.large,

              '&::before': {
                content: "''",
                position: 'absolute',
                zIndex: 1,
                background: `url(${mascotHanging}) no-repeat center center`,
                width: 67,
                height: 89,
                top: -theme.space.small,
                right: theme.space.base,
                transition: `opacity ${theme.timing.base} ${theme.transition.base}`,
                opacity: !isFlipped && 0,
              },
            },
          ]}
        >
          {employee.cardBlurb && (
            <div css={{ marginBottom: theme.space.base }}>
              <Data>{employee.cardBlurb}</Data>
            </div>
          )}
          {employee.social && (
            <Inline css={{ justifyContent: 'center' }}>
              {employee.social.map(({ service, url }) => (
                <ListItem key={service}>
                  <SocialIcon
                    url={url}
                    service={service}
                    onClick={(e) => e.stopPropagation()}
                  />
                </ListItem>
              ))}
            </Inline>
          )}
        </div>
      </div>
      <div
        css={{
          paddingTop: theme.space.base,
          textAlign: 'center',
        }}
      >
        <div
          css={[
            text.heading(theme),
            {
              letterSpacing: theme.letterSpacing.small,
              textTransform: 'uppercase',
              fontWeight: theme.fontWeight.bold,
            },
          ]}
        >
          {employee.title}
        </div>
        <div css={{ fontSize: theme.fontSize.small }}>{employee.position}</div>
      </div>
    </div>
  )
}

EmployeeCard.propTypes = {
  employee: PropTypes.object.isRequired,
}

// Converts the icon data Open Weather Map sends down to one in our library
const getWeatherIcon = (icon) => {
  if (icon.includes('snow') || icon.includes('sleet')) {
    return 'weather__snow'
  }

  if (
    icon.includes('rain') ||
    icon.includes('thunderstorm') ||
    icon.includes('drizzle')
  ) {
    return 'weather__rain'
  }

  if (
    icon.includes('mist') ||
    icon.includes('Smoke') ||
    icon.includes('Haze') ||
    icon.includes('dust')
  ) {
    return 'weather__wind'
  }

  if (icon.includes('clouds')) {
    return 'weather__cloud'
  }

  return 'weather__sun'
}

const OtherLocationsWrapper = ({ locations }) => {
  const theme = useTheme()

  const [randomLocations, setRandomLocations] = useState(locations)

  useInterval(() => {
    setRandomLocations(shuffle(randomLocations))
  }, 5000)

  return (
    <div
      className="u-contrast"
      css={{
        background: `linear-gradient(to right, ${theme.color.dark.base}, ${tint(
          0.15,
          theme.color.dark.base
        )})`,
        display: 'grid',
        gridRowColumns: '1fr 2px 1fr',
        borderBottomLeftRadius: theme.borderRadius.base,
        borderBottomRightRadius: theme.borderRadius.base,
        position: 'relative',
        // This is a variation of our `shadowEmboss` utility to create a "mask" over the existing one
        boxShadow: `
    1px 0 ${rgba(theme.color.light.base, 0.15)}, 1px 0 ${theme.color.dark.base},
    3px 0 0 ${theme.color.dark.base}, 4px 0 0 ${
          theme.color.dark.base
        }, 5px 0 0 ${theme.color.dark.base},
    6px 0 0 ${theme.color.dark.base}, 7px 0 0 ${
          theme.color.dark.base
        }, 8px 0 0 ${theme.color.dark.base},
    9px 0 0 ${theme.color.dark.base}, 10px 0 0 ${
          theme.color.dark.base
        }, 11px 0 0 ${theme.color.dark.base},
    12px 0 0 ${theme.color.dark.base}, 13px 0 0 ${
          theme.color.dark.base
        }, 14px 0 0 ${theme.color.dark.base},
    15px 0 0 ${theme.color.dark.base}`,

        [theme.mq.medium]: {
          gridTemplateColumns: '1fr 2px 1fr',
        },
      }}
    >
      <div css={{ textAlign: 'center' }}>
        <WeatherWidgetLocation
          location={randomLocations[0].location}
          weather={randomLocations[0].weather}
          size="small"
        />
      </div>
      <div
        css={{
          background: theme.color.dark.base,

          [theme.mq.small__max]: {
            height: 2,
          },
        }}
      />
      <div css={{ textAlign: 'center' }}>
        <WeatherWidgetLocation
          location={randomLocations[1].location}
          weather={randomLocations[1].weather}
          size="small"
        />
      </div>
    </div>
  )
}

OtherLocationsWrapper.propTypes = {
  locations: PropTypes.array.isRequired,
}

const WeatherWidgetLocation = ({ location, weather, size }) => {
  const theme = useTheme()

  const currentTime = dateFn()
    .utcOffset(weather.utcOffset / 60)
    .format('h:mm A')

  return (
    <div
      css={{
        padding: theme.space.base,
        textAlign: 'center',
        display: 'inline-block',
      }}
    >
      <div
        css={[
          size === 'large' && [theme.fontSize.h1__fluid],
          size === 'small' && {
            fontSize: theme.fontSize.h4,
          },
        ]}
      >
        <span
          css={[
            text.heading(theme),
            textGradientUnderline(theme, { size: 'small' }),
          ]}
        >
          {location.location}
        </span>
      </div>
      <div
        css={[
          text.heading(theme),
          {
            marginTop: size === 'large' ? theme.space.xsmall : 0,
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: theme.space.small,
            textAlign: 'center',
            fontSize:
              size === 'large' ? theme.fontSize.h4 : theme.fontSize.medium,
          },
        ]}
      >
        <div>{currentTime}</div>
        <div>
          <Icon
            icon={getWeatherIcon(weather.description)}
            css={{ marginRight: theme.space.small }}
          />
          {Math.round(weather.temp)} &#176;
        </div>
      </div>
    </div>
  )
}

WeatherWidgetLocation.propTypes = {
  location: PropTypes.object.isRequired,
  size: PropTypes.oneOf(['small', 'large']),
  weather: PropTypes.object,
}

// https://openweathermap.org/
const WeatherWidget = ({ locations, ...rest }) => {
  const theme = useTheme()

  const ids = locations.reduce((accumulator, currentValue) => {
    accumulator.push(currentValue.openWeatherMapId)

    return accumulator
  }, [])

  const weather = useAsync(async () => {
    const response = await axios.post('/.netlify/functions/weather', {
      ids,
      environment: process.env.NODE_ENV,
    })

    return response.data.weather
  }, [])

  if (weather.loading) {
    return <Loading />
  }

  if (weather.error || weather.value.length === 0) {
    return null
  }

  const locationsWithWeather = locations.map((location, index) => ({
    location,
    weather: Array.isArray(weather.value[index].weather)
      ? {
          description: weather.value[index].weather[0].description,
          temp: weather.value[index].main.temp,
          utcOffset: weather.value[index].sys.timezone,
        }
      : {
          description: weather.value[index].weather.description,
          temp: weather.value[index].main.temp,
          utcOffset: weather.value[index].sys.timezone,
        },
  }))

  return (
    <div
      css={[
        shadowEmboss(theme, {
          size: '20px',
        }),
        {
          borderRadius: theme.borderRadius.base,
          display: 'grid',
          gridTemplateRows: '1fr auto',
        },
      ]}
      {...rest}
    >
      <div
        css={{
          alignSelf: 'center',
          justifySelf: 'center',
        }}
      >
        <WeatherWidgetLocation
          location={locationsWithWeather[0].location}
          weather={locationsWithWeather[0].weather}
          size="large"
        />
      </div>
      <OtherLocationsWrapper locations={locationsWithWeather.slice(1)} />
    </div>
  )
}

WeatherWidget.propTypes = {
  locations: PropTypes.array.isRequired,
}

const CareersList = () => {
  const theme = useTheme()

  const [isLoading, setLoading] = useState(true)
  const [jobs, setJobs] = useState([])

  const fetchJobs = async () => {
    const [, data] = await to(
      axios.get(
        'https://boards-api.greenhouse.io/v1/boards/gremlin/departments?render_as=list'
      )
    )

    if (data) {
      setJobs(
        orderBy(data.data.departments, (department) => department.jobs, 'desc')
      )
      setLoading(false)
    } else {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchJobs()
  }, [])

  const renderJobs = () => {
    const totalJobs = jobs.reduce((accumulator, currentValue) => {
      return accumulator.concat(currentValue.jobs)
    }, [])

    if (totalJobs.length > 0) {
      return (
        <BlockGrid
          size={{ medium: 3 }}
          gutter={{ base: 'large' }}
          css={{ justifyContent: 'center' }}
        >
          {jobs.map((department) => {
            if (!department.jobs.length) return

            return (
              <ListItem key={department.name}>
                <h4
                  css={[
                    textGradientUnderline(theme, {
                      size: 'small',
                    }),
                  ]}
                >
                  {department.name}
                </h4>
                <List
                  space="base"
                  css={{ margin: '0 !important', padding: '0 !important' }}
                >
                  {department.jobs.map((job) => (
                    <ListItem key={job.internal_job_id}>
                      <TextLink
                        css={{
                          display: 'block',
                          borderRadius: theme.borderRadius.base,
                          border: theme.border.base,
                          boxShadow: theme.boxShadow.large,
                          padding: theme.space.base,
                          position: 'relative',
                          transformOrigin: 'center center',
                          transition: `transform ${theme.timing.base} ${theme.transition.bounce}`,
                          lineHeight: theme.lineHeight.small,

                          '&:hover, &:focus': {
                            transform: 'scale(1.025)',

                            '.Careers__JobTitle': {
                              color: shade(0.1, theme.brand.blue),
                            },
                          },
                        }}
                        href={job.absolute_url}
                      >
                        <div
                          css={[
                            text.heading(theme),
                            {
                              color: theme.fontColor.heading,
                              fontSize: theme.fontSize.base,
                              letterSpacing: theme.letterSpacing.small,
                              textTransform: 'uppercase',
                              fontWeight: theme.fontWeight.bold,

                              transition: `color ${theme.timing.base} ${theme.transition.bounce}`,
                            },
                          ]}
                          className="Careers__JobTitle"
                        >
                          {job.title}
                        </div>
                        <div
                          css={{
                            color: theme.fontColor.base,
                            fontSize: theme.fontSize.small,
                            marginTop: theme.space.small,
                          }}
                        >
                          {job.location.name}
                        </div>
                      </TextLink>
                    </ListItem>
                  ))}
                </List>
              </ListItem>
            )
          })}
        </BlockGrid>
      )
    }

    return (
      <p css={{ textAlign: 'center', color: theme.fontColor.muted }}>
        No jobs are currently available.
      </p>
    )
  }

  useUpdateEffect(() => {
    renderJobs()
  }, [isLoading])

  if (isLoading) {
    return <Loading />
  }

  return renderJobs()
}

const Team = ({
  data: {
    file: {
      childMdx: { frontmatter: pageData },
    },
  },
}) => {
  const theme = useTheme()

  return (
    <BaseLayout footerProps={{ hideCTA: true }}>
      <HelmetSEO title={pageData.title} description={pageData.description} />
      <Hero
        css={{
          backgroundColor: theme.color.dark.base,

          '&::after': {
            content: "''",
            background: `url(${cloudinaryTransform(
              hero
            )}) no-repeat center center`,
            backgroundSize: 'cover',
            opacity: theme.opacity.base * 0.75,
            filter: 'grayscale(100%)',
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            zIndex: 1,
          },
        }}
      >
        <HeroContent>
          <Container>
            <HeroTitleWrapper>
              <HeroTitle
                size="large"
                css={{
                  lineHeight: theme.lineHeight.small,

                  span: [
                    textGradientUnderline(theme),
                    {
                      [theme.mq.medium]: {
                        backgroundSize: `100% 10px}`,
                      },
                    },
                  ],
                }}
              >
                <Data>{pageData.hero.heading}</Data>
              </HeroTitle>
              <HeroTitleSub>
                <Data>{pageData.hero.blurb}</Data>
              </HeroTitleSub>
            </HeroTitleWrapper>
          </Container>
        </HeroContent>
      </Hero>
      <Section
        css={{
          [theme.mq.medium]: {
            background: `linear-gradient(to right, ${theme.brand.teal}, ${tint(
              0.05,
              theme.color.primary.base
            )} 50%, ${theme.color.light.base} 50%)`,
          },
        }}
      >
        <Container>
          <div
            css={{
              display: 'grid',
              gap: theme.space.large,

              [theme.mq.medium]: {
                rowGap: theme.space.xlarge * 2,
                gridTemplateColumns: 'repeat(2, 1fr)',
                gap: theme.space.xlarge,
              },
            }}
          >
            <div>
              <h2
                css={{
                  color: theme.fontColor.heading,
                  textAlign: 'center',

                  [theme.mq.medium]: {
                    textAlign: 'left',
                    color: theme.contrast.base,
                  },
                }}
              >
                <Data>{pageData.introduction.heading}</Data>
              </h2>
            </div>
            <div>
              <Data>{pageData.introduction.blurb}</Data>
            </div>
            <div>
              <h2
                css={{
                  color: theme.fontColor.heading,
                  textAlign: 'center',

                  [theme.mq.medium]: {
                    textAlign: 'left',
                    color: theme.contrast.base,
                    ...stickyIt(theme),
                  },
                }}
              >
                <Data>{pageData.values.heading}</Data>
              </h2>
            </div>
            <div>
              <div
                css={{
                  display: 'grid',
                  gap: theme.space.medium,

                  [theme.mq.medium]: {
                    gridTemplateColumns: 'repeat(2, 1fr)',
                    rowGap: theme.space.large,
                  },
                }}
              >
                {pageData.values.items.map((item, index) => (
                  <div
                    css={{
                      display: 'grid',
                      gap: theme.space.xsmall,
                      alignContent: 'start',
                    }}
                    key={item.heading}
                  >
                    <div
                      css={{
                        fontSize: theme.fontSize.medium,
                        color: theme.color.primary.base,
                        fontWeight: theme.fontWeight.bold,
                      }}
                    >
                      {index + 1}.
                    </div>
                    <div
                      css={[
                        text.heading(theme),
                        {
                          color: theme.fontColor.heading,
                          fontSize: theme.fontSize.medium,
                          letterSpacing: theme.letterSpacing.small,
                          textTransform: 'uppercase',
                          fontWeight: theme.fontWeight.bold,
                        },
                      ]}
                    >
                      <Data>{item.heading}</Data>
                    </div>
                    <div>
                      <Data>{item.blurb}</Data>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </Container>
      </Section>
      <TeamSection team={pageData.team} />
      <Section space="xlarge">
        <Container>
          <div
            css={{
              display: 'grid',
              gap: theme.space.base,
              rowGap: theme.space.large,
              marginBottom: theme.space.xlarge,

              [theme.mq.medium]: {
                gridTemplateColumns: 'repeat(3, 1fr)',
              },
            }}
          >
            <div
              css={{
                [theme.mq.medium]: {
                  paddingRight: StylesTeamVariables(theme).gridOffset,
                },
              }}
            >
              <h3>
                <Data>{pageData.location.heading}</Data>
              </h3>
              <div
                css={[
                  text.heading(theme),
                  {
                    color: theme.fontColor.heading,
                    fontSize: theme.fontSize.base,
                    letterSpacing: theme.letterSpacing.small,
                    textTransform: 'uppercase',
                    fontWeight: theme.fontWeight.bold,
                  },
                ]}
              >
                <Data>{pageData.location.blurb}</Data>
              </div>
            </div>
            <div
              css={{
                gridRow: '2',

                [theme.mq.medium]: {
                  paddingRight: StylesTeamVariables(theme).gridOffset,
                },
              }}
            >
              <h4>
                <Data>{pageData.location.headingSub}</Data>
              </h4>
            </div>
            <WeatherWidget
              locations={pageData.location.weather}
              css={{
                [theme.mq.medium]: {
                  gridRow: '1 / span 2',
                  gridColumn: '2 / span 2',
                },
              }}
            />
          </div>
        </Container>
      </Section>
      <Section
        space="xlarge"
        css={[
          sectionSeparator(theme, {
            start: theme.brand.blue,
            stop: theme.brand.purple,
          }),
        ]}
      >
        <SectionTitle title={<Data>{pageData.investors.heading}</Data>} />
        <OverflowScroll size="xlarge">
          {pageData.investors.items.map((item) => (
            <Image
              key={item.name}
              src={item.logo}
              alt={item.name}
              css={{
                height: theme.height.small,
              }}
            />
          ))}
        </OverflowScroll>
      </Section>
      <Section
        id="careers"
        space="xlarge"
        css={[
          sectionSeparator(theme, {
            start: theme.brand.blue,
            stop: theme.brand.purple,
          }),
          {
            background: generateGradient({
              start: theme.color.light.base,
              stop: theme.color.panel.dark,
              position: 'to bottom',
            }),
          },
        ]}
      >
        <Container>
          <SectionTitle
            as="h2"
            title={<Data>{pageData.careers.heading}</Data>}
          />
          <CareersList />
          <div css={{ marginTop: theme.space.xlarge, textAlign: 'center' }}>
            <SectionTitle
              title={<Data>{pageData.careers.bottom.heading}</Data>}
              sub={<Data>{pageData.careers.bottom.blurb}</Data>}
              css={{
                marginBottom: `${theme.space.large}px !important`,
              }}
            />
            <Button
              type="primary"
              url={`mailto:${pageData.careers.bottom.email}`}
            >
              {pageData.careers.bottom.email}
            </Button>
          </div>
        </Container>
      </Section>
    </BaseLayout>
  )
}

Team.propTypes = {
  data: PropTypes.object.isRequired,
}

export default Team

export const pageQuery = graphql`
  query TeamPageData {
    file(name: { eq: "team" }, absolutePath: { regex: "/src/data/" }) {
      childMdx {
        frontmatter {
          title
          description
          hero {
            heading
          }
          introduction {
            heading
            blurb
          }
          values {
            heading
            items {
              heading
              blurb
            }
          }
          team {
            heading
            blurb
            employees {
              employee {
                frontmatter {
                  title
                  portrait
                  position
                  cardBlurb
                  social {
                    service
                    url
                  }
                }
              }
            }
          }
          location {
            heading
            blurb
            headingSub
            weather {
              location
              openWeatherMapId
            }
          }
          investors {
            heading
            items {
              name
              logo
            }
          }
          careers {
            heading
            bottom {
              heading
              blurb
              email
            }
          }
        }
      }
    }
  }
`
